/** Hook per caricare dati da API, con stato di caricamento, errore e refresh. */
import { useCallback, useEffect, useRef, useState } from 'react'

export interface AsyncState<T> {
  data: T | null
  error: unknown
  loading: boolean
  /**
   * Ricarica, e **si può attendere**.
   *
   * Restituiva `void`, e sembrava un dettaglio. Non lo era: chi ricaricava dopo
   * un'azione non poteva sapere quando i dati nuovi fossero arrivati, quindi
   * mostrava il risultato dell'azione accanto alla lista **di prima**. Sulla
   * scheda di un asset si è visto «10 reperti aperti» sopra un elenco che ne
   * conteneva uno, e per giunta quello appena chiuso: due numeri veri che
   * insieme dicono una cosa falsa.
   */
  reload: () => Promise<void>
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  // La funzione cambia a ogni render: la dipendenza esplicita è `deps`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps)

  // Vivo finché il componente è montato: senza, una ricarica che finisce dopo
  // che l'utente ha cambiato pagina scriverebbe stato su un componente che non
  // c'è più.
  const vivo = useRef(true)
  useEffect(() => {
    vivo.current = true
    return () => {
      vivo.current = false
    }
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const result = await run()
      if (vivo.current) {
        setData(result)
        setError(null)
      }
    } catch (err) {
      if (vivo.current) setError(err)
    } finally {
      if (vivo.current) setLoading(false)
    }
  }, [run])

  useEffect(() => {
    void reload()
  }, [reload])

  return { data, error, loading, reload }
}

/** Come useAsync, ma ricarica automaticamente a intervalli regolari. */
export function usePolling<T>(fn: () => Promise<T>, intervalMs: number, deps: unknown[] = []) {
  const state = useAsync(fn, deps)
  const { reload } = state

  useEffect(() => {
    const id = setInterval(reload, intervalMs)
    return () => clearInterval(id)
  }, [reload, intervalMs])

  return state
}

/**
 * Una lettura «dal vivo» con la copia del Control Plane: la prima chiamata
 * accetta la copia (la pagina si apre subito, con la data di raccolta), e
 * `reload` — il pulsante «Aggiorna» — chiede i dati freschi (`refresh`).
 */
export function useVivo<T extends { refreshing?: boolean }>(
  carica: (refresh: boolean) => Promise<T>, deps: unknown[] = [],
): AsyncState<T> & { rileggiCopia: () => Promise<void> } {
  const forza = useRef(false)
  const stato = useAsync(() => {
    const fresco = forza.current
    forza.current = false
    return carica(fresco)
  }, deps)
  const reload = useCallback(() => {
    forza.current = true
    return stato.reload()
  }, [stato.reload])
  // L'aggiornamento gira in sottofondo sul Control Plane: finché la risposta
  // dice `refreshing`, si richiede ogni pochi secondi (con la copia, senza
  // far ripartire niente) fino ai dati nuovi. Al più tre minuti.
  const [attesa, setAttesa] = useState(false)
  const refreshing = Boolean(stato.data?.refreshing)
  useEffect(() => {
    if (!refreshing) {
      setAttesa(false)
      return
    }
    setAttesa(true)
    let giri = 0
    const id = setInterval(() => {
      giri += 1
      if (giri > 60) {
        clearInterval(id)
        setAttesa(false)
        return
      }
      void stato.reload()
    }, 3000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing, stato.data?.refreshing === undefined ? 0 : 1])
  // `rileggiCopia` chiede la copia senza far ripartire l'interrogazione: dopo
  // una scrittura il Control Plane l'ha già ritoccata e rilegge da sé.
  return { ...stato, loading: stato.loading || attesa, reload, rileggiCopia: stato.reload }
}

